import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../../../hooks';
import { safePostMessage } from '../../../../utils';
import { createChatMessageFromUI } from '../../../../utils/chat-message';
import { runChatbotOpenRequests } from '../../../../utils/chatbotOpenRequests';
import { SenderType } from '../../../../types/chat-message';
import type {
  BlockContent,
  CommandVariable,
  MessageButton,
  TextBlockPayload,
} from '../../../../types/chat-message';
import type { ChatMessage } from '../../../../types';
import {
  Conversation,
  AgentSelector,
  AgentSwitchNotification,
  InputMessage,
  ChatHeader,
  ChatSideSheet,
  PdfListOverlay,
  CustomTooltip,
  GlobalToast,
} from '@pages/chatbot/components';
import { ChatAgentType } from '@constants/chat-agents';
import type { AiDisplaySetting } from '@services/chatService';
import './EmbedBox.scss';

const isHiddenButton = (commandVariables?: CommandVariable[]): boolean =>
  commandVariables?.some((v) => v.name === 'hidden_message' && v.value === 'true') ?? false;

const collectMessageButtons = (msg: ChatMessage): MessageButton[] => {
  const fromBlocks =
    msg.blockContents?.flatMap((block) => {
      if (block.type !== 'TEXT') return [];
      return (block.payload as TextBlockPayload).buttons || [];
    }) ?? [];
  return [...(msg.buttons || []), ...fromBlocks];
};

const findLiveCancelOrderButton = (log: ChatMessage[]): MessageButton | undefined => {
  for (let i = log.length - 1; i >= 0; i--) {
    const msg = log[i];
    if (msg.senderType !== SenderType.BOT || msg._isHistory) continue;
    const hasLiveOrder = msg.blockContents?.some((b) => b.type === 'ORDER_INFO');
    if (!hasLiveOrder) continue;
    return collectMessageButtons(msg).find((btn) => btn.intent === 'cancel_order');
  }
  return undefined;
};

const EmbedBox: React.FC = () => {
  const { t } = useTranslation();
  const {
    activeAgent,
    handleAgentChange,
    currentChatLog,
    currentHasChatHistory,
    connected,
    inputValue,
    listening,
    onChangeInput,
    handleMicrophoneClick,
    stopMicAndDiscard,
    handleChatSubmit,
    handleCommandClick,
    handleSwitchAgent,
    handleSkipSwitch,
    switchNotification,
    chatRef,
    agentConfig,
    onReadyConversation,
    isSTTSupported,
    isThinking,
    setIsThinking,
    commands,
    loadMoreHistory,
    loadNewerHistory,
    isFetchingMore,
    handleForwardToAgent,
    redirectData,
    closeRedirectOverlay,
    // Audio
    isAudioEnabled,
    toggleAudio,
    setAudioEnabled,
    isCommandsLoading,
    // Quick Selection
    quickSelectionData,
    handleInstructionClick,
    handleQuickFunctionClick,
    closeQuickSelection,
    handleQuickSelectionSelect,
    quickFunctions,
    quickFunctionSearchList,
    instructions,
    handleRateMessage,
    handleRetryUpload,
    quotedMessage,
    stopBotSpeaking,
    handleQuoteMessage,
    handleCancelQuote,
    scrollToQuotedMessage,
    markAllAsHistory,
    isChatInputDisabled,
    isBotSpeaking,
    hasPendingAudio,
    connectionStatuses,
    needsManualReconnect,
    manualReconnect,
    hasCSKHNotification,
    toastMessage,
    setToastMessage,
    chatSessionVersion,
    addMessage,
    updateMessage,
    getCurrentChatLog,
    user,
    handleStopStreaming,
    clearStoppedMessages,
    sendChangeModeMessage,
    refreshConversationId,
    startNewChatSession,
    loadCurrentChatSession,
    selectChatSession,
  } = useChat();

  // Track countdown active state from OrderCard via window event
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  /** Bump mỗi lần mở chatbot → refetch quick-suggestions (quyền có thể đổi) */
  const [quickSuggestionsRefreshKey, setQuickSuggestionsRefreshKey] = useState(0);
  /** Kết quả personalization từ runChatbotOpenRequests */
  const [aiDisplayFromOpen, setAiDisplayFromOpen] = useState<AiDisplaySetting | null>(null);
  const [aiDisplayOpenVersion, setAiDisplayOpenVersion] = useState(0);
  /** Bump mỗi lần đóng chatbot → thu gọn welcome / quick-suggestions */
  const [welcomeResetKey, setWelcomeResetKey] = useState(0);
  const pendingAgentRef = useRef<ChatAgentType | null>(null);
  const sideSheetContainerRef = useRef<HTMLDivElement | null>(null);
  const activeAgentRef = useRef(activeAgent);
  activeAgentRef.current = activeAgent;

  const handleChatbotOpen = useCallback(() => {
    void runChatbotOpenRequests({
      activeAgent: activeAgentRef.current,
      loadCurrentChatSession,
      refreshWelcomeSuggestions: () => {
        setQuickSuggestionsRefreshKey((prev) => prev + 1);
      },
    }).then(({ aiDisplay }) => {
      if (aiDisplay) {
        setAiDisplayFromOpen(aiDisplay);
        setAiDisplayOpenVersion((prev) => prev + 1);
      }
    });
  }, [loadCurrentChatSession]);

  /**
   * Đóng chatbot (nút X / CHAT_TOGGLE close) → đưa UI về default.
   * Thêm reset state mới vào đây (không persist sang lần mở sau).
   * Hiện tại:
   * - đóng side sheet
   * - thu gọn quick-suggestions (welcomeResetKey)
   * - bật lại âm thanh
   */
  const resetUiOnClose = () => {
    setIsSideSheetOpen(false);
    setWelcomeResetKey((prev) => prev + 1);
    setAudioEnabled(true);
  };

  useEffect(() => {
    const handler = (e: Event) => {
      setIsCountdownActive((e as CustomEvent).detail?.isActive ?? false);
    };
    window.addEventListener('order-countdown-change', handler);
    return () => window.removeEventListener('order-countdown-change', handler);
  }, []);

  // Track typewriter stream animation active state
  const [isTypewriterAnimating, setIsTypewriterAnimating] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      setIsTypewriterAnimating((e as CustomEvent).detail?.isAnimating ?? false);
    };
    window.addEventListener('typewriter-animation-change', handler);
    return () => window.removeEventListener('typewriter-animation-change', handler);
  }, []);

  useEffect(() => {
    setIsTypewriterAnimating(false);
  }, [activeAgent]);

  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'CHAT_TOGGLE') {
        if (event.data.isOpen === false) {
          console.log('[EmbedBox] Received CHAT_TOGGLE (close) from parent, stopping voice and mic');
          resetUiOnClose();
          stopBotSpeaking();
          stopMicAndDiscard();
          handleStopStreaming();
          clearStoppedMessages();
          // Re-open always lands on Hỏi đáp (AI)
          pendingAgentRef.current = null;
          if (activeAgent !== ChatAgentType.AI) {
            handleAgentChange(ChatAgentType.AI);
          }
        } else if (event.data.isOpen === true) {
          console.log('[EmbedBox] Received CHAT_TOGGLE (open)');
          handleChatbotOpen();
        }
      }
    };
    window.addEventListener('message', handleWindowMessage);
    return () => window.removeEventListener('message', handleWindowMessage);
  }, [
    stopBotSpeaking,
    stopMicAndDiscard,
    handleStopStreaming,
    clearStoppedMessages,
    activeAgent,
    handleAgentChange,
    handleChatbotOpen,
  ]);

  // Mở lại chatbot (đã từng ẩn) → bootstrap lại requests
  useEffect(() => {
    const rootEl = document.querySelector('.embed-box');
    if (!rootEl) return;

    let hasBeenHidden = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          hasBeenHidden = true;
          return;
        }
        if (!hasBeenHidden) return;

        hasBeenHidden = false;
        console.log('[EmbedBox] Chatbot visible again');
        window.dispatchEvent(new CustomEvent('chatbot:opened'));
        handleChatbotOpen();
      });
    }, { threshold: 0.1 });

    observer.observe(rootEl);
    return () => {
      observer.disconnect();
    };
  }, [handleChatbotOpen]);

  // Track bot streaming state (thinking, active socket stream, or typewriter animating)
  const chatLog = getCurrentChatLog(activeAgent);
  const lastMsg = chatLog.length > 0 ? chatLog[chatLog.length - 1] : null;
  const isBotStreaming = isThinking || 
                         (lastMsg?.senderType === SenderType.BOT && lastMsg?.isFinal === false && !lastMsg?._isHistory) ||
                         isTypewriterAnimating;

  // Intercept agent change - if countdown active, only send CHANGE_MODE to ws and block switching
  const handleAgentChangeGuarded = (agent: typeof activeAgent) => {
    if (isCountdownActive) {
      pendingAgentRef.current = agent;
      // Gửi request CHANGE_MODE lên ws của web
      sendChangeModeMessage(agent);
      return;
    }
    handleAgentChange(agent);
  };

  // Handle "Có" - add a fresh OrderCard message with restarted countdown
  const handleConfirmStay = (messageId: string, blockContents?: BlockContent[]) => {
    updateMessage(activeAgent, messageId, { isConfirmDisabled: true });
    pendingAgentRef.current = null;
    if (blockContents && blockContents.length > 0) {
      addMessage(activeAgent, createChatMessageFromUI({
        text: '',
        user: SenderType.BOT,
        isRead: true,
        agentType: activeAgent,
        time: new Date().toISOString(),
        blockContents,
      }));
    }
  };

  // Handle "Không" - switch to pending agent
  const handleConfirmLeave = (messageId: string, pendingAgent: typeof activeAgent) => {
    console.log('[Confirm Leave] Clicked. messageId:', messageId, 'pendingAgent:', pendingAgent, 'pendingAgentRef.current:', pendingAgentRef.current, 'activeAgent:', activeAgent);
    updateMessage(activeAgent, messageId, { isConfirmDisabled: true });

    // Gửi tin nhắn "Không" lên websocket
    console.log('[Confirm Leave] Submitting chat "Không"...');
    handleChatSubmit("Không");

    // Chừn agent từ ref (luôn có giá trị mới nhất, không bị stale closure)
    const targetAgent = pendingAgentRef.current || pendingAgent;
    console.log('[Confirm Leave] Target agent determined:', targetAgent);
    if (targetAgent && targetAgent !== activeAgent) {
      console.log('[Confirm Leave] Switching agent to:', targetAgent);
      handleAgentChange(targetAgent);
    } else {
      console.warn('[Confirm Leave] Switch blocked: targetAgent is invalid or same as activeAgent');
    }
    pendingAgentRef.current = null;
  };

  const handleClose = () => {
    resetUiOnClose();
    onChangeInput('');
    markAllAsHistory();
    stopMicAndDiscard();
    stopBotSpeaking();
    handleStopStreaming();
    clearStoppedMessages();
    pendingAgentRef.current = null;
    // Next open defaults to Hỏi đáp (AI)
    if (activeAgent !== ChatAgentType.AI) {
      handleAgentChange(ChatAgentType.AI);
    }
    // Huỷ countdown trong OrderCard khi đóng chatbot
    window.dispatchEvent(new CustomEvent('chatbot-close'));
    safePostMessage({
      type: 'CHAT_TOGGLE',
      isOpen: false,
    });
  };

  // Before opening new chat, cancel live order if any
  const handleStartNewChat = async () => {
    await refreshConversationId();
    await startNewChatSession();
  };

  const handleClickSetting = () => {
    if (!user?.permissions?.store_settings_all_view) {
      setToastMessage({
        text: t('common.noPermissions'),
        type: 'error',
      });
      return;
    }

    const targetRoute = '/setting/tendoo-ai';
    safePostMessage({
      type: 'NAVIGATE_TO',
      path: targetRoute
    });
  };

  return (
    <div className="embed-box">
      {toastMessage && (
        <GlobalToast 
          message={{
            ...toastMessage,
            onAction: toastMessage.type === 'cskh' ? () => {
              handleAgentChangeGuarded(ChatAgentType.CSKH);
              setToastMessage(null);
            } : undefined,
            actionLabel: toastMessage.type === 'cskh' ? 'Xem chi tiết' : undefined,
          }}
          onClose={() => setToastMessage(null)}
        />
      )}

      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <img src={`${process.env.PUBLIC_URL || ''}/background.svg`} alt="background" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
      <div
        ref={sideSheetContainerRef}
        style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 30, pointerEvents: isSideSheetOpen ? 'auto' : 'none' }}
      />
      <ChatHeader
        onClose={handleClose}
        onMenuClick={() => setIsSideSheetOpen(true)}
        showNewChat={activeAgent === ChatAgentType.AI}
        onNewChatClick={() => {
          void handleStartNewChat();
        }}
        isAudioEnabled={isAudioEnabled}
        onToggleAudio={toggleAudio}
        isBotSpeaking={isBotSpeaking}
        onClickSetting={handleClickSetting}
      />

      <ChatSideSheet
        open={isSideSheetOpen}
        onClose={() => setIsSideSheetOpen(false)}
        getContainer={sideSheetContainerRef.current ?? undefined}
        isAudioEnabled={isAudioEnabled}
        onToggleAudio={toggleAudio}
        isBotSpeaking={isBotSpeaking}
        typeChat={activeAgent}
        sessionsRefreshKey={chatSessionVersion}
        aiDisplayFromOpen={aiDisplayFromOpen}
        aiDisplayOpenVersion={aiDisplayOpenVersion}
        onNewChat={() => {
          void handleStartNewChat();
        }}
        onSelectConversation={(conversation) => {
          void selectChatSession(conversation.id, ChatAgentType.AI);
        }}
      />

      <AgentSelector
        activeAgent={activeAgent}
        onAgentChange={handleAgentChangeGuarded}
        hasNotification={hasCSKHNotification}
        isThinking={isThinking}
        onSettingClick={() => {
          const targetRoute = '/setting/tendoo-ai';
          if (window.parent && window.parent !== window) {
            safePostMessage({
              type: 'NAVIGATE_TO',
              path: targetRoute
            });
          } else {
            console.log('Navigate requested in standalone mode:', targetRoute);
            window.open(targetRoute, '_blank');
          }
        }}
      />

      <div className="embed-box-layout">
        <div className="embed-box-upper">
          <div className="embed-box-main">
            <div className="embed-box-chat-section">
              <Conversation
                connected={connected}
                chatLog={currentChatLog}
                chatSessionVersion={chatSessionVersion}
                quickSuggestionsRefreshKey={quickSuggestionsRefreshKey}
                welcomeResetKey={welcomeResetKey}
                handleChatSubmit={handleChatSubmit}
                onReadyConversation={onReadyConversation}
                chatRef={chatRef as React.RefObject<HTMLDivElement | null>}
                hasChatHistory={currentHasChatHistory}
                activeAgent={activeAgent}
                isThinking={isThinking}
                setIsThinking={setIsThinking}
                onScrollToBottom={() => chatRef.current?.scrollIntoView({ behavior: 'smooth' })}
                loadMoreHistory={loadMoreHistory}
                loadNewerHistory={loadNewerHistory}
                isFetchingMore={isFetchingMore}
                handleAgentChange={handleAgentChange}
                handleForwardToAgent={handleForwardToAgent}
                handleRateMessage={handleRateMessage}
                handleRetryUpload={handleRetryUpload}
                handleQuoteMessage={handleQuoteMessage}
                scrollToQuotedMessage={scrollToQuotedMessage}
                needsManualReconnect={needsManualReconnect[activeAgent]}
                isReconnecting={connectionStatuses[activeAgent] === 'connecting'}
                onManualReconnect={() => manualReconnect(activeAgent)}
                commands={commands}
                isCommandsLoading={isCommandsLoading}
                handleCommandClick={handleCommandClick}
                onConfirmStay={handleConfirmStay}
                onConfirmLeave={handleConfirmLeave}
                setToastMessage={setToastMessage}
              />

              {switchNotification && (
                <AgentSwitchNotification
                  notification={switchNotification}
                  onSwitch={handleSwitchAgent}
                  onSkip={handleSkipSwitch}
                />
              )}

            </div>
          </div>

        
           
        </div>
        
        {/* Footer Area - Commands and Input */}
        <div className="embed-box-footer">
          <InputMessage
            onSendMessage={(message, images, component) => {
              void handleChatSubmit(message, undefined, undefined, images, component);
            }}
            setToastMessage={setToastMessage}
            handleMicrophoneClick={handleMicrophoneClick}
            stopMicAndDiscard={stopMicAndDiscard}
            inputValue={inputValue}
            listening={listening}
            onChangeInput={onChangeInput}
            placeholder={agentConfig?.placeholder ? t(agentConfig.placeholder) : t('common.chatPlaceholder')}
            hasCommands={isCommandsLoading || (commands && commands.length > 0)}
            activeAgent={activeAgent}
            quickFunctions={quickFunctions}
            quickFunctionSearchList={quickFunctionSearchList}
            instructions={instructions}
            onSelectQuickFunction={(item) => handleChatSubmit(item.name, item.id)}
            onSelectInstruction={(item) => {
              const text =
                typeof item === 'string'
                  ? item
                  : item.function_name || item.question || item.title || item.name;
              handleChatSubmit(text);
            }}
            commands={commands}
            isCommandsLoading={isCommandsLoading}
            onSelectCommand={(item) => handleCommandClick(item.name, item.id)}
            quotedMessage={quotedMessage}
            onCancelQuote={handleCancelQuote}
            isChatInputDisabled={isChatInputDisabled}
            isBotSpeaking={isBotSpeaking}
            isThinking={isThinking}
            isMicDisabled={toastMessage?.type === 'error'}
            isBotStreaming={isBotStreaming}
            onStopStreaming={handleStopStreaming}
            permissions={user?.permissions}
            isOwner={user?.isOwner}
          />
        </div>
      </div>
    </div>

      <CustomTooltip id="chatbot-tooltip" place="top" />


      {/* PDF List Panel - fullscreen overlay */}
      {redirectData && (
        <PdfListOverlay
          data={redirectData}
          onClose={closeRedirectOverlay}
        />
      )}
      <div id="chat-overlay-portal-root" className="embed-box-portal-root" />
    </div>
  );
};

export default EmbedBox;
